import * as echarts from 'echarts'

export type ChartKind = 'pie' | 'bar' | 'line' | 'graph' | 'sankey' | 'scatter'
export type ChartSpec = {
  kind: ChartKind
  title: string
  items?: Array<{ name: string; value: number }>
  nodes?: Array<Record<string, unknown>>
  links?: Array<Record<string, unknown>>
  points?: Array<Record<string, unknown>>
  categories?: string[]
  xName?: string
  yName?: string
}
export type ChartBundle = { type: 'chart_bundle'; title?: string; summary?: Record<string, unknown>; charts: ChartSpec[] }

const chartKinds = new Set<ChartKind>(['pie', 'bar', 'line', 'graph', 'sankey', 'scatter'])

export function normalizeChartBundle(value: unknown): ChartBundle | null {
  if (!value || typeof value !== 'object') return null
  const source = isObject((value as Record<string, unknown>).chart_bundle)
    ? (value as Record<string, unknown>).chart_bundle as Record<string, unknown>
    : value as Record<string, unknown>
  const rawCharts = Array.isArray(source.charts) ? source.charts : []
  if (source.type !== 'chart_bundle' && !rawCharts.length) return null
  const charts = rawCharts.flatMap(normalizeChart)
  return charts.length ? { type: 'chart_bundle', title: stringValue(source.title), summary: isObject(source.summary) ? source.summary : undefined, charts } : null
}

export function parseChartBundle(content: string): ChartBundle | null {
  const text = content.trim()
  const candidates = [text]
  const fenced = text.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/i)
  if (fenced) candidates.push(fenced[1])
  for (const candidate of candidates) {
    try {
      const parsed = normalizeChartBundle(JSON.parse(candidate))
      if (parsed) return parsed
    } catch { /* ordinary Markdown */ }
  }
  return null
}

export function renderAssistantCharts(root: ParentNode): void {
  root.querySelectorAll<HTMLElement>('[data-assistant-chart]').forEach((element) => {
    const bundle = parseAttributeBundle(element.dataset.assistantChart)
    if (!bundle) return
    const chart = echarts.init(element)
    const spec = bundle.charts[0]
    chart.setOption(toOption(bundle, spec), true)
    const resize = () => chart.resize()
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(resize)
    observer?.observe(element)
    element.dataset.chartMounted = 'true'
    ;(element as HTMLElement & { __assistantChartCleanup?: () => void }).__assistantChartCleanup = () => {
      observer?.disconnect()
      window.removeEventListener('resize', resize)
      chart.dispose()
    }
    window.addEventListener('resize', resize)
  })
}

export function disposeAssistantCharts(root: ParentNode): void {
  root.querySelectorAll<HTMLElement>('[data-assistant-chart][data-chart-mounted="true"]').forEach((element) => {
    ;(element as HTMLElement & { __assistantChartCleanup?: () => void }).__assistantChartCleanup?.()
  })
}

function parseAttributeBundle(value?: string): ChartBundle | null {
  if (!value) return null
  try { return normalizeChartBundle(JSON.parse(value)) } catch { return null }
}

function normalizeChart(value: unknown): ChartSpec[] {
  if (!isObject(value)) return []
  const kind = String(value.kind || value.type || '').trim() as ChartKind
  if (!chartKinds.has(kind)) return []
  const title = stringValue(value.title) || ({ graph: '关系图', sankey: '关系流向', pie: '分布饼图', bar: '分布柱状图', line: '趋势图', scatter: '聚类分布' }[kind])
  if (kind === 'graph' || kind === 'sankey') return [{ kind, title, nodes: arrayValue(value.nodes), links: arrayValue(value.links), categories: arrayValue(value.categories).map(String) }]
  if (kind === 'scatter') return [{ kind, title, points: arrayValue(value.points || value.data), xName: stringValue(value.xName), yName: stringValue(value.yName) }]
  return [{ kind, title, items: arrayValue(value.items || value.data).map((item, index) => {
    if (isObject(item)) return { name: stringValue(item.name) || String(index + 1), value: Number(item.value) }
    return { name: String(index + 1), value: Number(item) }
  }).filter((item) => item.name && Number.isFinite(item.value)) }]
}

function toOption(bundle: ChartBundle, spec: ChartSpec): echarts.EChartsOption {
  const title = { text: spec.title || bundle.title || '助手分析图', left: 'center' as const, top: 8, textStyle: { color: '#24332e', fontSize: 14, fontWeight: 'normal' as const } }
  if (spec.kind === 'graph') return { backgroundColor: '#ffffff', title, tooltip: { trigger: 'item', textStyle: { color: '#24332e' } }, legend: spec.categories?.length ? [{ data: spec.categories, bottom: 0, textStyle: { color: '#24332e' } }] : undefined, series: [{ type: 'graph', layout: 'force', roam: true, draggable: true, data: spec.nodes || [], links: spec.links || [], categories: (spec.categories || []).map((name) => ({ name })), force: { repulsion: 180, edgeLength: 100 }, label: { show: true, position: 'right', color: '#24332e', textBorderColor: '#ffffff', textBorderWidth: 2 }, itemStyle: { color: '#d9eee6', borderColor: '#2f8066', borderWidth: 1 }, lineStyle: { color: '#71948a', curveness: 0.15 }, edgeLabel: { show: true, color: '#24332e', backgroundColor: '#ffffff', padding: [2, 3] }, emphasis: { focus: 'adjacency', label: { color: '#17241f' } } }] }
  if (spec.kind === 'sankey') return { backgroundColor: '#ffffff', title, tooltip: { trigger: 'item', textStyle: { color: '#24332e' } }, series: [{ type: 'sankey', draggable: true, emphasis: { focus: 'adjacency' }, nodeAlign: 'left', data: spec.nodes || [], links: spec.links || [], lineStyle: { color: '#71948a', curveness: 0.5 }, itemStyle: { color: '#d9eee6', borderColor: '#2f8066' }, label: { color: '#24332e', textBorderColor: '#ffffff', textBorderWidth: 2 } }] }
  if (spec.kind === 'scatter') return { title, tooltip: { trigger: 'item' }, grid: { top: 52, bottom: 42, left: 54, right: 20 }, xAxis: { name: spec.xName || '' }, yAxis: { name: spec.yName || '' }, series: [{ type: 'scatter', symbolSize: 14, data: spec.points || [] }] }
  const items = spec.items || []
  return { title, tooltip: { trigger: spec.kind === 'pie' ? 'item' : 'axis' }, grid: spec.kind === 'pie' ? undefined : { top: 52, bottom: 42, left: 54, right: 20 }, xAxis: spec.kind === 'pie' ? undefined : { type: 'category', data: items.map((item) => item.name) }, yAxis: spec.kind === 'pie' ? undefined : { type: 'value' }, series: [{ type: spec.kind, radius: spec.kind === 'pie' ? ['36%', '68%'] : undefined, data: spec.kind === 'pie' ? items : items.map((item) => item.value), smooth: spec.kind === 'line', label: spec.kind === 'pie' ? { show: true, formatter: '{b}: {d}%' } : undefined }] }
}

function isObject(value: unknown): value is Record<string, any> { return Boolean(value) && Object.prototype.toString.call(value) === '[object Object]' }
function arrayValue(value: unknown): any[] { return Array.isArray(value) ? value : [] }
function stringValue(value: unknown): string | undefined { return typeof value === 'string' && value.trim() ? value.trim() : undefined }
